import { gql, useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import { useNavigate } from '@tanstack/react-router';
import { type FormikProps, useFormik } from 'formik';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import AssetCount from 'src/components/AssetCount';
import AkomiDialog from 'src/components/dialogs/AkomiDialog';
import AkomiDialogActions from 'src/components/dialogs/AkomiDialogActions';
import { skeletonSx } from 'src/components/dialogs/DialogSkeleton';
import SelectWithLabel from 'src/components/SelectWithLabel';
import { CAN_EDIT_SLUG } from 'src/constants';
import { allCollections, updateGallery } from 'src/dynamoQueries/collections';
import { onCreateGallerySuccess, onErrorCreateGallery } from 'src/helpers';
import { cleanPayload } from 'src/helpers/common';
import { useBuildQuery } from 'src/hooks/build';
import { useCustomToast } from 'src/hooks/menu';

interface GalleryItem {
  id: string;
  name: string;
}

interface AllCollectionsData {
  allCollections: {
    edges: Array<{
      node: GalleryItem;
    }>;
  };
}

interface UpdateGalleryFormValues {
  collectionId: string;
}

interface UpdateGalleryMutationData {
  updateGallery: {
    success: boolean;
  };
}

interface UpdateGalleryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  assetIds?: string[];
}

const UpdateGalleryDialog = ({
  isOpen,
  onClose,
  assetIds = [],
}: UpdateGalleryDialogProps): React.JSX.Element | null => {
  const navigate = useNavigate();
  const header = 'Update Gallery';
  const { toastr } = useCustomToast();

  const { data, loading, error } = useBuildQuery<AllCollectionsData>(
    allCollections(true) as unknown as Record<string, unknown>,
    {
      variables: {
        collectionType: 'GALLERY',
        hasPermission: CAN_EDIT_SLUG,
      },
      fetchPolicy: 'network-only',
    },
  );
  const galleries = (cleanPayload(data) || []) as GalleryItem[];
  const buildUpdateGalleryMutation = () => {
    const m = updateGallery(true);
    const mutation = jsonToGraphQLQuery(m, { pretty: true });
    return gql(mutation);
  };

  const [updateGalleryMutation, { loading: updateGalleryLoading, error: errorUpdatingGallery }] =
    useMutation<UpdateGalleryMutationData>(buildUpdateGalleryMutation());

  const onSave = (values: UpdateGalleryFormValues) => {
    updateGalleryMutation({
      variables: {
        assetIds,
        collectionId: values.collectionId,
      },
    }).then(() => {
      onCreateGallerySuccess({
        navigate,
        galleryId: values.collectionId,
      });
      onClose();
    });
  };

  const formik: FormikProps<UpdateGalleryFormValues> = useFormik<UpdateGalleryFormValues>({
    initialValues: {
      collectionId: '',
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      onSave(values);
    },
  });

  if (error) {
    toastr.error('Error while loading galleries');
    onClose();
    return null;
  }

  if (errorUpdatingGallery) {
    onErrorCreateGallery({ error: errorUpdatingGallery });
    onClose();
    return null;
  }

  return (
    <AkomiDialog
      open={isOpen}
      onClose={onClose}
      title={header}
      maxWidth="xs"
      loading={loading}
      skeleton={
        <Box>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ ...skeletonSx, width: 100, height: 12, mb: '6px' }} />
            <Box sx={{ ...skeletonSx, width: '100%', height: 36 }} />
          </Box>
          <Box sx={{ ...skeletonSx, width: 90, height: 12 }} />
        </Box>
      }
      actions={
        <AkomiDialogActions
          variant="confirm"
          primaryLabel="Update Gallery"
          onPrimary={() => formik.handleSubmit()}
          submitting={updateGalleryLoading}
        />
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <div style={{ marginBottom: '16px' }}>
          <SelectWithLabel
            // biome-ignore lint/suspicious/noExplicitAny: SelectWithLabel expects FormikInstance with mediaGroupId, but UpdateGalleryFormValues uses collectionId
            formik={formik as any}
            label={'Select Gallery'}
            items={galleries}
            name="collectionId"
          />
        </div>
        <AssetCount assetIds={assetIds} create={false} />
      </form>
    </AkomiDialog>
  );
};

export default UpdateGalleryDialog;
